'use client';

import { useTranslations } from 'next-intl';
import { Eye, Trash2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { PermissionGate } from '@/features/auth';
import type { SortingSession, SortingSessionStatus } from '../types';

interface SortingCardProps {
  session: SortingSession;
  onView: (session: SortingSession) => void;
  onDelete: (session: SortingSession) => void;
}

const STATUS_STYLES: Record<SortingSessionStatus, string> = {
  pending:
    'bg-amber-100 text-amber-800 hover:bg-amber-100 dark:bg-amber-900 dark:text-amber-200',
  in_progress:
    'bg-blue-100 text-blue-800 hover:bg-blue-100 dark:bg-blue-900 dark:text-blue-200',
  completed:
    'bg-green-100 text-green-800 hover:bg-green-100 dark:bg-green-900 dark:text-green-200'
};

export function SortingCard({ session, onView, onDelete }: SortingCardProps) {
  const t = useTranslations('sorting');

  const statusLabels: Record<SortingSessionStatus, string> = {
    pending: t('statusPending'),
    in_progress: t('statusInProgress'),
    completed: t('statusCompleted')
  };

  const sorted = session.sorted_containers ?? 0;
  const total = session.total_containers ?? 0;
  const refused = session.refused_containers ?? 0;
  const progressPct = total > 0 ? Math.round((sorted / total) * 100) : 0;

  return (
    <Card className='border-border hover:border-primary/30 transition-colors'>
      <CardContent className='p-6'>
        {/* Top: box number + status badge */}
        <div className='mb-4 flex items-start justify-between'>
          <div>
            <h3 className='font-mono text-lg font-bold'>
              {session.box?.box_number ?? '-'}
            </h3>
            {session.creator && (
              <p className='text-muted-foreground text-xs'>
                {t('createdBy')}: {session.creator.name}
              </p>
            )}
          </div>
          <Badge
            variant='secondary'
            className={cn('shrink-0', STATUS_STYLES[session.status])}
          >
            {statusLabels[session.status] ?? session.status}
          </Badge>
        </div>

        {/* Middle: progress + stats */}
        <div className='mb-4 space-y-3'>
          {/* Progress bar */}
          <div>
            <div className='mb-1 flex items-center justify-between text-sm'>
              <span className='text-muted-foreground'>{t('progress')}</span>
              <span className='font-medium'>
                {sorted}/{total} ({progressPct}%)
              </span>
            </div>
            <div className='bg-muted h-2 overflow-hidden rounded-full'>
              <div
                className='bg-primary h-full rounded-full transition-all'
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>

          <div className='text-muted-foreground grid grid-cols-2 gap-2 text-sm'>
            <div>
              <span>{t('refusedContainers')}: </span>
              <span className='text-foreground font-medium'>{refused}</span>
            </div>
            <div>
              <span>{t('startedAt')}: </span>
              <span className='text-foreground font-medium'>
                {session.started_at
                  ? new Date(session.started_at).toLocaleDateString(undefined, {
                      month: 'short',
                      day: 'numeric'
                    })
                  : t('notStarted')}
              </span>
            </div>
            {session.completed_at && (
              <div className='col-span-2'>
                <span>{t('completedAt')}: </span>
                <span className='text-foreground font-medium'>
                  {new Date(session.completed_at).toLocaleDateString(
                    undefined,
                    {
                      month: 'short',
                      day: 'numeric'
                    }
                  )}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Bottom: action buttons */}
        <div className='flex items-center justify-end gap-1 border-t pt-3'>
          <Button
            variant='ghost'
            size='icon'
            className='h-8 w-8'
            onClick={() => onView(session)}
          >
            <Eye className='h-4 w-4' />
          </Button>
          <PermissionGate permissions={['sorting.delete']}>
            <Button
              variant='ghost'
              size='icon'
              className='text-destructive h-8 w-8'
              onClick={() => onDelete(session)}
            >
              <Trash2 className='h-4 w-4' />
            </Button>
          </PermissionGate>
        </div>
      </CardContent>
    </Card>
  );
}
